import React, { useState, useCallback } from "react";
import { AnimatePresence, motion } from "motion/react";
import BulkUploadJobsHeader from "./components/BulkUploadJobsHeader";
import JobCard from "./components/JobCard";
import JobRecordsTable from "../shared/components/Table/JobRecordsTable";
import "./BulkUploadJobs.css";
import useUploadsQueries from "../store/uploads/uploadsQueries";
import { statusOptions, typeOptions } from "../constants/bulkJobConstants";
import loader from "../assets/gifs/loader.gif";

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.4,
      ease: "easeOut",
    },
  }),
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.2,
      ease: "easeIn",
    },
  },
};

const BulkUploadJobs = () => {
  const { useBulkJobLogs } = useUploadsQueries();

  const [expandedJobId, setExpandedJobId] = useState(null);

  const handleToggleDetails = useCallback((job) => {
    setExpandedJobId((prevId) => (prevId === job.job_id ? null : job.job_id));
  }, []);

  const [jobId, setJobId] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState(
    statusOptions.filter((opt) => opt.value !== "All").map((opt) => opt.value)
  );
  const [selectedTypes, setSelectedTypes] = useState(
    typeOptions.filter((opt) => opt.value !== "All").map((opt) => opt.value)
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const payload = {
    status: selectedStatuses,
    job_id: jobId,
    job_type: selectedTypes,
    page: currentPage,
    page_size: itemsPerPage,
  };

  const { data, isLoading, refetch, isFetching } = useBulkJobLogs(payload);

  const jobLogs = data?.logs || [];
  const totalJobs = data?.total_count || 0;

  const handleJobIdChange = useCallback((value) => {
    setJobId(value);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((newlySelectedStatuses) => {
    setSelectedStatuses(newlySelectedStatuses);
    setCurrentPage(1);
  }, []);

  const handleTypeChange = useCallback((newlySelectedTypes) => {
    setSelectedTypes(newlySelectedTypes);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  const renderContent = () => {
    if (isLoading || isFetching) {
      return (
        <div className="bulk-upload-jobs-error-loading-container">
          <img
            src={loader}
            alt="Loading..."
            style={{ height: "200px", width: "200px" }}
          />
        </div>
      );
    }
    if (payload.status.length === 0 || payload.job_type.length === 0) {
      return (
        <p className="bulk-upload-jobs-error-loading-container">
          Filters are required to view the jobs
        </p>
      );
    }
    if (jobLogs.length > 0) {
      return (
        <div className="jobs-list" key={currentPage}>
          <AnimatePresence>
            {jobLogs.map((job, index) => (
              <motion.div
                key={job.job_id}
                layout
                variants={itemVariants}
                custom={index}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <JobCard
                  job={job}
                  expandedJobId={expandedJobId}
                  handleToggleDetails={handleToggleDetails}
                />
                <AnimatePresence>
                  {expandedJobId === job.job_id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      style={{ overflow: "hidden" }}
                    >
                      <div style={{ paddingTop: "16px" }}>
                        <JobRecordsTable
                          jobId={job.job_id}
                          jobType={job.upload_type}
                          status={job.status}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      );
    }
    return (
      <p className="bulk-upload-jobs-error-loading-container">No jobs found</p>
    );
  };

  return (
    <div className="bulk-upload-jobs-container">
      <BulkUploadJobsHeader
        jobId={jobId}
        handleJobIdChange={handleJobIdChange}
        isLoading={isLoading}
        selectedStatuses={selectedStatuses}
        handleStatusChange={handleStatusChange}
        selectedTypes={selectedTypes}
        handleTypeChange={handleTypeChange}
        totalItems={totalJobs}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        handlePageChange={handlePageChange}
        handleItemsPerPageChange={handleItemsPerPageChange}
        handleRefresh={refetch}
      />
      {renderContent()}
    </div>
  );
};

export default BulkUploadJobs;
